export type FailureCode<Domain extends string> = `${Domain}.${string}`;

export interface DomainFailure<
  Domain extends string,
  Code extends FailureCode<Domain>,
  Operation extends string,
> {
  cause?: unknown;
  code: Code;
  operation: Operation;
  status?: number;
  technicalMessage?: string;
}

type DomainFailureFor<Domain extends string> = DomainFailure<
  Domain,
  FailureCode<Domain>,
  string
>;

export type FailureMessageMap<Failure extends { code: string }> = {
  [Code in Failure["code"]]: (
    failure: Extract<Failure, { code: Code }>,
  ) => string;
};

export interface FailureMessageResolver<Failure extends { code: string }> {
  (failure: Failure): string;
  (failure: null): null;
  (failure: Failure | null): string | null;
}

export class DomainFailureError<
  Failure extends DomainFailure<string, FailureCode<string>, string>,
> extends Error {
  override readonly cause?: unknown;
  readonly code: Failure["code"];
  readonly detail: Failure;

  constructor(detail: Failure, name = "DomainFailureError") {
    super(detail.technicalMessage ?? detail.code, { cause: detail.cause });
    this.name = name;
    this.cause = detail.cause;
    this.code = detail.code;
    this.detail = detail;
  }
}

export function defineFailureDomain<Domain extends string>(domain: Domain) {
  function create<Failure extends DomainFailureFor<Domain>>(
    detail: Failure,
  ): Failure {
    if (!detail.code.startsWith(`${domain}.`)) {
      throw new Error(`Failure code must start with ${domain}.`);
    }

    return detail;
  }

  function createError<Failure extends DomainFailureFor<Domain>>(
    detail: Failure,
    name?: string,
  ) {
    return new DomainFailureError(create(detail), name);
  }

  return {
    create,
    createError,
    domain,
  } as const;
}

export function defineFailureMessageMap<Failure extends { code: string }>(
  map: FailureMessageMap<Failure>,
): FailureMessageResolver<Failure> {
  function getFailureMessage(failure: Failure): string;
  function getFailureMessage(failure: null): null;
  function getFailureMessage(failure: Failure | null): string | null;
  function getFailureMessage(failure: Failure | null): string | null {
    if (failure === null) {
      return null;
    }

    const code = failure.code as Failure["code"];
    const resolveMessage = map[code] as (failure: Failure) => string;

    return resolveMessage(failure);
  }

  return getFailureMessage;
}
